import { useQuery } from '@apollo/client';
import { memo, useCallback, useEffect, useState } from 'react';

import {
  AnalyticsJobFormCtaText,
  AnalyticsJobFormSection,
  AnalyticsPage
} from 'lib/analytics/constants';
import { DataDogRumAgent } from 'lib/datadog/initializeDatadog';
import { useJobFormControllerContext } from 'lib/oneflare-job-form/utils/context';
import { AUTOCOMPLETE_LOCATION } from 'queries/shared/autocompleteLocation';
import { extractPostcode } from 'shared/utils/helpers';
import type { CategoryModel, LocationModel } from 'types/shared';

import { useFullPageJobFormRedirectExperiment } from './experiments/useFullPageJobFormRedirectExperiment';
import * as Styled from './styled/HeroAutoComplete';
import { useAutoCompleteSelectBehaviour } from './utils';

type AutoCompleteCategory = Pick<
  CategoryModel,
  'slug' | 'singular' | 'plural' | 'id' | 'noun' | 'verb' | 'name'
>;

type AutocompleteHeroProps = {
  kind: 'light' | 'primary';
  category: AutoCompleteCategory;
  location: Pick<LocationModel, 'autocomplete' | 'postcode' | 'state' | 'city'>;
  removeDropdown?: boolean;
  placeholder?: string;
  inExperimentalCategory?: boolean;
  jobFormInitiatedPageName: AnalyticsPage;
};

const HeroAutoComplete = ({
  placeholder,
  kind,
  location,
  category,
  removeDropdown,
  inExperimentalCategory,
  jobFormInitiatedPageName
}: AutocompleteHeroProps) => {
  const { controller } = useJobFormControllerContext();
  const [locationOptions, setLocationOptions] = useState(
    location?.autocomplete ?? []
  );
  const [selectedLocationOption, setSelectedLocationOption] = useState({
    value: removeDropdown ? null : location?.postcode ?? null,
    valid: false,
    id: null
  });

  const handleJobFormLaunch = useCallback(
    ({
      locationId,
      locationName
    }: {
      locationId: string | number;
      locationName: string;
    }) => {
      const categoryId = category?.id;
      const categoryName = category?.name;
      let jobFormPostcode = locationName;
      // Extract postcode from locationName if it contains a comma
      if (locationName && locationName.includes(',')) {
        jobFormPostcode = extractPostcode(locationName);
      }
      return controller?.open({
        categoryId,
        categoryName,
        locationId: Number(locationId),
        locationName,
        postcode: jobFormPostcode,
        page: jobFormInitiatedPageName,
        section: AnalyticsJobFormSection.HeroBanner,
        ctaText: AnalyticsJobFormCtaText.Go
      });
    },
    [category, controller, jobFormInitiatedPageName]
  );

  const { refetch } = useQuery(AUTOCOMPLETE_LOCATION, {
    fetchPolicy: 'network-only',
    variables: { term: selectedLocationOption?.value ?? '' },
    onError: (error) => {
      DataDogRumAgent.addRumError(
        error,
        'Shared | Hero | AUTOCOMPLETE_LOCATION query'
      );
    }
  });

  const handleChange = async ({ target: { value } }) => {
    setSelectedLocationOption({ value, valid: false, id: null });
    if (value && value.trim().length && refetch) {
      try {
        const { data } = await refetch({ term: value });
        setLocationOptions(data?.autocompleteLocation ?? []);
      } catch (error) {
        DataDogRumAgent.addRumError(
          error,
          'Shared | Hero | AUTOCOMPLETE_LOCATION query'
        );
        setLocationOptions([]);
      }
    }
  };

  const handleFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.target.select();
  };

  const handleHeroCtaClick = useCallback(async () => {
    let locationPrefill = location?.postcode;
    const { value, valid } = selectedLocationOption;
    if (value) {
      locationPrefill = valid ? extractPostcode(value) : value;
    }
    handleJobFormLaunch({
      locationId: selectedLocationOption?.id,
      locationName: locationPrefill
    });
  }, [selectedLocationOption, handleJobFormLaunch, location?.postcode]);
  
  const { redirectToFullPageJobForm } = useFullPageJobFormRedirectExperiment(inExperimentalCategory);
  const { handleSelect } = useAutoCompleteSelectBehaviour({
    redirectToFullPageJobForm,
    setSelectedLocationOption,
    locationOptions,
    category,
    handleJobFormLaunch
  });

  useEffect(() => {
    window.onblur = () => {
      setSelectedLocationOption((prev) => ({ ...prev, valid: false }));
    };

    const autoComplete = document.querySelector(
      '#location-hero'
    ) as HTMLInputElement;
    
    if (autoComplete) {
      autoComplete.focus();
    }

    return () => {
      window.onblur = null;
    };
  }, []);

  return (
    <Styled.AutoCompleteWrapper>
      <Styled.AutoCompleteInput
        $kind={kind}
        type="text"
        id="location-hero"
        items={locationOptions.map(({ value }) => ({
          name: value,
          value
        }))}
        onChange={handleChange}
        onFocus={handleFocus}
        onSelect={handleSelect}
        placeholder={placeholder}
        value={selectedLocationOption?.value ?? ''}
      />
      <HeroCta
        onClick={handleHeroCtaClick}
        link={`/get-free-quotes/${category.slug}`}
        label="GO"
        redirectToFullPageJobForm={redirectToFullPageJobForm}
      />
    </Styled.AutoCompleteWrapper>
  );
};

const HeroCta = ({
  onClick,
  link,
  label,
  redirectToFullPageJobForm
}) => {
  if (redirectToFullPageJobForm) {
    return <Styled.HeroLinkCta kind="primary" link={link}>{label}</Styled.HeroLinkCta>;
  }
  return <Styled.HeroCta label={label} kind="primary" onClick={onClick} />;
};

export default memo(HeroAutoComplete);
export type { AutocompleteHeroProps, AutoCompleteCategory };
